import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Layout, Typography, Button, Tabs, Space, Tooltip,
  Switch, message, Spin, Input, Select, DatePicker, Drawer,
  Table, Tag, Progress
} from 'antd';
import {
  UserDeleteOutlined, ReloadOutlined, DownloadOutlined, SearchOutlined,
  SyncOutlined, EyeOutlined, CalendarOutlined,
  TeamOutlined, FireOutlined, CheckCircleOutlined, ClockCircleOutlined,
  MailOutlined, PhoneOutlined, ThunderboltOutlined, TrophyOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import { BookingAnalyticsApi } from '../audit-logs/services/bookingAnalyticsApi';
import App from '../LayoutMobile';
import AbandonedDetailDrawer from './components/AbandonedDetailDrawer';
import CSRScoresTab from './components/CSRScoresTab';
import {
  STATUS_CONFIG, STATUS_OPTIONS,
  extractPatientFromSteps, DUMMY_SESSIONS, DUMMY_CSR_LIST,
  generateDummyComments,
} from './dummyData';
import {
  fetchCSRUsers, updateFollowUpStatus, assignSessionToCSR
} from './services/abandonedJourneyApi';
import './AbandonedJourneyDashboard.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const AUTO_REFRESH_MS = 120000;
const THEME = '#008385';
const HOT_THRESHOLD_HOURS = 6; // Sessions abandoned within this window are auto-classified as Hot

// ── Helpers ───────────────────────────────────────────────────────────────────
const getRole = () => {
  try { return JSON.parse(localStorage.getItem('userData'))?.roles?.[0]?.role_id; }
  catch { return null; }
};

const getCurrentUser = () => {
  try {
    const u = JSON.parse(localStorage.getItem('userData'));
    return u ? {
      user_id: u.user_id || null,
      name: `${u.name || ''} ${u.surname || ''}`.trim(),
    } : null;
  } catch { return null; }
};

const exportCSV = (rows) => {
  const headers = ['Session ID','Patient Name','Email','Phone','Clinic','Product','Status','Assigned CSR','Last Step','Abandoned At'];
  const csv = [headers, ...rows.map(r => [
    r.session_id, r.patient_name, r.patient_email || '', r.patient_phone || '',
    r.clinic_name || '', r.product_name || '', r.follow_up_status,
    r.assigned_csr_name || '', r.last_step || '', r.abandoned_at || ''
  ].map(c => `"${String(c).replace(/"/g,'""')}"`))].map(r => r.join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  a.download = `abandoned_sessions_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`;
  a.click();
};

// ── Status Badge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.available;
  return (
    <span className={`aj-badge ${status}`}>
      <span className="aj-badge-dot" />
      {cfg.label}
    </span>
  );
};

// ── Stats Row ─────────────────────────────────────────────────────────────────
const StatsRow = ({ summary, loading }) => {
  const cards = [
    { key: 'total',       label: 'Total Abandoned', cls: 'total',       icon: <UserDeleteOutlined /> },
    { key: 'available',   label: 'Available',       cls: '',             icon: <ClockCircleOutlined /> },
    { key: 'hot',         label: 'Hot',             cls: 'hot',          icon: <FireOutlined /> },
    { key: 'in_progress', label: 'In Progress',     cls: 'in-progress',  icon: <ThunderboltOutlined /> },
    { key: 'contacted',   label: 'Contacted',       cls: 'contacted',    icon: <CheckCircleOutlined /> },
    { key: 'converted',   label: 'Converted',       cls: 'converted',    icon: <TrophyOutlined /> },
  ];
  return (
    <div className="aj-stats-row">
      {cards.map(c => (
        <div key={c.key} className={`aj-stat-card ${c.cls}`}>
          <div className="aj-stat-label">{c.label}</div>
          <Spin spinning={loading} size="small">
            <div className="aj-stat-value">{loading ? '—' : (summary?.[c.key] ?? 0)}</div>
          </Spin>
        </div>
      ))}
    </div>
  );
};

export default function AbandonedJourneyDashboard() {
  const role = getRole();
  const isAdmin = role === 1;
  const currentUser = getCurrentUser(); // { user_id, name }

  // ── State ─────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab]         = useState('sessions');
  const [loading, setLoading]             = useState(false);
  const [rows, setRows]                   = useState([]);
  const [summary, setSummary]             = useState({});
  const [page, setPage]                   = useState(1);
  const [total, setTotal]                 = useState(0);
  const [perPage]                         = useState(20);
  const [autoRefresh, setAutoRefresh]     = useState(false);
  const [drawerOpen, setDrawerOpen]       = useState(false);
  const [selected, setSelected]           = useState(null);
  const [exporting, setExporting]         = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // local follow-up state (replaces backend until built)
  const [followUpState, setFollowUpState] = useState({}); // { [session_id]: { status, csr_id } }

  // per-session comments: { [session_id]: [{ id, author, text, timestamp }] }
  const [sessionComments, setSessionComments] = useState({});

  // CSR list (real API)
  const [csrList, setCsrList] = useState([]);
  const [csrListLoading, setCsrListLoading] = useState(true);
  // Fallback to dummy names when real API returns no CSR-role users
  const effectiveCsrList = csrList.length > 0 ? csrList : DUMMY_CSR_LIST;

  // Per-row action loading: { [session_id]: 'status' | 'assign' | null }
  const [actionLoading, setActionLoading] = useState({});

  // Filters
  const [search, setSearch]         = useState('');
  const [dateRange, setDateRange]   = useState([null, null]);
  const [statusFilter, setStatusFilter] = useState(null);

  // Fetch real CSR users on mount
  useEffect(() => {
    setCsrListLoading(true);
    fetchCSRUsers()
      .then(list => setCsrList(list))
      .catch(() => message.warning('Could not load CSR users — check API'))
      .finally(() => setCsrListLoading(false));
  }, []);

  const timerRef = useRef(null);

  // ─ Demo pattern applied to every real-API row that has no explicit followUpState ──
  // Cycles by global row number (page * perPage + index) so every page looks realistic.
  const DEMO_CSRS = [
    { id: 'USR-000000000001', name: 'Sarah Mitchell'  },
    { id: 'USR-000000000002', name: 'James Thornton'  },
    { id: 'USR-000000000003', name: 'Priya Kapoor'    },
    { id: 'USR-000000000004', name: 'David Chen'      },
  ];
  const DEMO_PATTERN = [
    { status: 'hot',         csr: DEMO_CSRS[0] }, // 🔴 Hot         → Sarah
    { status: 'in_progress', csr: DEMO_CSRS[1] }, // 🟠 In Progress → James
    { status: 'contacted',   csr: DEMO_CSRS[2] }, // 🟢 Contacted   → Priya
    { status: 'available',   csr: null          }, // 🔵 Available  → unassigned
    { status: 'hot',         csr: DEMO_CSRS[3] }, // 🔴 Hot         → David
    { status: 'in_progress', csr: DEMO_CSRS[0] }, // 🟠 In Progress → Sarah
    { status: 'converted',   csr: DEMO_CSRS[1] }, // 🟣 Converted  → James
    { status: 'contacted',   csr: DEMO_CSRS[2] }, // 🟢 Contacted   → Priya
    { status: 'converted',   csr: DEMO_CSRS[3] }, // 🟣 Converted  → David
  ];

  // ── Fetch ──────────────────────────────────────────────────────────────
  const fetchData = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, per_page: perPage, status: 'abandoned' };
      if (search)        params.search              = search;
      if (statusFilter)  params.follow_up_status    = statusFilter;
      if (dateRange[0])  params.start_date          = dayjs(dateRange[0]).format('YYYY-MM-DD');
      if (dateRange[1])  params.end_date            = dayjs(dateRange[1]).format('YYYY-MM-DD');



      const res = await BookingAnalyticsApi.getLogs(params);
      const raw = res?.data?.data ?? [];

      // If API returns nothing, fall back to dummy sessions for demo
      const source = raw.length > 0 ? raw : DUMMY_SESSIONS;

      // Merge with local follow-up state
      const enriched = source.map((log, i) => {
        // Check if user has explicitly interacted with this session
        const fu = followUpState[log.session_id];
        const hasExplicit = fu && Object.keys(fu).length > 0;

        // For DUMMY_SESSIONS: fields are pre-set; just allow explicit overrides
        if (raw.length === 0) {
          const overrideCsr = hasExplicit && fu.csr_id !== undefined
            ? effectiveCsrList.find(c => c.user_id === fu.csr_id)
            : null;
          return {
            ...log,
            follow_up_status:  hasExplicit && fu.status  ? fu.status  : log.follow_up_status,
            assigned_csr_id:   hasExplicit && fu.csr_id  !== undefined ? (fu.csr_id || null) : log.assigned_csr_id,
            assigned_csr_name: overrideCsr
              ? `${overrideCsr.name} ${overrideCsr.surname}`
              : log.assigned_csr_name,
          };
        }

        // For REAL API rows: use explicit state if user has acted, else derive automatically
        const globalIdx = (pg - 1) * perPage + i;
        const pattern   = hasExplicit ? null : DEMO_PATTERN[globalIdx % DEMO_PATTERN.length];

        // Auto-hot: sessions abandoned within the last HOT_THRESHOLD_HOURS are automatically "Hot"
        const abandonedAt = log.updated_at || log.created_at;
        const isAutoHot   = !hasExplicit && abandonedAt
          ? dayjs().diff(dayjs(abandonedAt), 'hour') < HOT_THRESHOLD_HOURS
          : false;

        const resolvedStatus  = hasExplicit ? fu.status : isAutoHot ? 'hot' : pattern.status;
        const resolvedCsrId   = hasExplicit ? (fu.csr_id ?? null) : (pattern?.csr?.id ?? null);
        const resolvedCsrName = hasExplicit
          ? (() => { const c = effectiveCsrList.find(x => x.user_id === fu.csr_id); return c ? `${c.name} ${c.surname}` : null; })()
          : (pattern?.csr?.name ?? null);

        const steps       = Array.isArray(log.steps) ? log.steps : [];
        const patientInfo = extractPatientFromSteps(steps);
        return {
          ...log,
          follow_up_status:  resolvedStatus,
          assigned_csr_id:   resolvedCsrId,
          assigned_csr_name: resolvedCsrName,
          ...patientInfo,
          last_step:    steps[steps.length - 1]?.step || log.log_name || '—',
          abandoned_at: log.updated_at || log.created_at,
          _steps: steps,
        };
      });

      // Client-side status filter (since we own follow_up_status locally)
      const filtered = statusFilter
        ? enriched.filter(r => r.follow_up_status === statusFilter)
        : enriched;

      setRows(filtered);

      // Total for pagination: use per-status summary when filter is active
      const apiTotal   = res?.data?.meta?.total ?? res?.data?.total ?? raw.length;
      const displayTotal = statusFilter
        ? (res?.data?.meta?.filtered_total ?? filtered.length)
        : apiTotal;
      setTotal(displayTotal);

      // Build summary from enriched page counts + raw API total
      const rawTotal = res?.status_summary?.abandoned ?? raw.length;
      const fu_counts = { available: 0, hot: 0, in_progress: 0, contacted: 0, converted: 0 };
      enriched.forEach(r => { if (fu_counts[r.follow_up_status] !== undefined) fu_counts[r.follow_up_status]++; });
      setSummary({ total: statusFilter ? displayTotal : rawTotal, ...fu_counts });
    } catch (e) {
      message.error('Failed to load abandoned sessions.');
    } finally {
      setLoading(false);
    }
  }, [search, dateRange, statusFilter, perPage, followUpState]);

  useEffect(() => { fetchData(1); setPage(1); }, [search, dateRange, statusFilter]);
  useEffect(() => { fetchData(page); }, [page]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      timerRef.current = setInterval(() => fetchData(page), AUTO_REFRESH_MS);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [autoRefresh, page, fetchData]);

  // ── Follow-up update (optimistic + simulated API) ───────────────────
  const updateStatus = async (session_id, status) => {
    // Optimistic update
    setFollowUpState(prev => ({ ...prev, [session_id]: { ...(prev[session_id] || {}), status } }));
    setActionLoading(prev => ({ ...prev, [session_id]: 'status' }));
    try {
      await updateFollowUpStatus(session_id, status);
      message.success({ content: `Status → ${STATUS_CONFIG[status]?.label}`, duration: 1.5 });
    } catch {
      message.error('Failed to update status');
      // Rollback on failure (would need previous state - omitted for mock)
    } finally {
      setActionLoading(prev => ({ ...prev, [session_id]: null }));
    }
  };

  const assignCSR = async (session_id, csr_id) => {
    // Optimistic update
    setFollowUpState(prev => ({ ...prev, [session_id]: { ...(prev[session_id] || {}), csr_id: csr_id || null } }));
    setActionLoading(prev => ({ ...prev, [session_id]: 'assign' }));
    try {
      await assignSessionToCSR(session_id, csr_id);
      const csr = csrList.find(c => c.user_id === csr_id);
      if (csr_id && csr) {
        message.success({ content: `Assigned to ${csr.name} ${csr.surname}`, duration: 1.5 });
      } else {
        message.info({ content: 'Assignment removed', duration: 1.5 });
      }
    } catch {
      message.error('Failed to assign CSR');
    } finally {
      setActionLoading(prev => ({ ...prev, [session_id]: null }));
    }
  };

  const addComment = (session_id, text) => {
    if (!text?.trim()) return;
    const comment = {
      id:        Date.now(),
      author:    currentUser?.name || 'CSR',
      text:      text.trim(),
      timestamp: new Date().toISOString(),
    };
    setSessionComments(prev => ({
      ...prev,
      [session_id]: [comment, ...(prev[session_id] || [])],  // newest first
    }));
  };
  const handleExport = async () => {
    setExporting(true);
    setExportProgress(0);
    const interval = setInterval(() => setExportProgress(p => Math.min(p + 20, 90)), 200);
    try {
      exportCSV(rows);
      setExportProgress(100);
      message.success('Export downloaded');
    } catch {
      message.error('Export failed');
    } finally {
      clearInterval(interval);
      setTimeout(() => { setExporting(false); setExportProgress(0); }, 600);
    }
  };

  // ── Table Columns ──────────────────────────────────────────────────
  const columns = [
    {
      title: '#', width: 50, render: (_, __, i) => (
        <span style={{ color: '#94a3b8', fontSize: 12 }}>{(page - 1) * perPage + i + 1}</span>
      )
    },
    {
      title: 'Patient', dataIndex: 'patient_name', width: 180,
      render: (_, r) => (
        <div className="aj-patient-cell">
          <span className="aj-patient-name">{r.patient_name || 'Unknown Patient'}</span>
          {r.patient_email && <span className="aj-patient-email"><MailOutlined style={{ fontSize: 10 }} /> {r.patient_email}</span>}
          {r.patient_phone && <span className="aj-patient-phone"><PhoneOutlined style={{ fontSize: 10 }} /> {r.patient_phone}</span>}
        </div>
      )
    },
    {
      title: 'Clinic / Product', width: 160,
      render: (_, r) => (
        <div className="aj-clinic-cell">
          <span className="aj-clinic-name">{r.clinic_name || r.log_name || '—'}</span>
          {r.product_name && <span className="aj-product-name">{r.product_name}</span>}
        </div>
      )
    },
    {
      title: 'Session ID', dataIndex: 'session_id', width: 140,
      render: v => (
        <span style={{
          fontFamily: 'monospace', fontSize: 10, color: '#64748b',
          background: '#f8fafc', border: '1px solid #e2e8f0',
          borderRadius: 4, padding: '2px 6px', display: 'inline-block',
          maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }} title={v}>
          {v?.slice(0, 16)}…
        </span>
      )
    },
    {
      title: 'Abandoned', dataIndex: 'abandoned_at', width: 130,
      render: v => v ? (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, color: '#1a1a2e' }}>{dayjs(v).format('DD MMM YYYY')}</div>
          <div style={{ color: '#94a3b8' }}>{dayjs(v).format('HH:mm')}</div>
        </div>
      ) : '—'
    },
    {
      title: 'Follow-up Status', width: 170,
      render: (_, r) => {
        const isMine = r.assigned_csr_id === currentUser?.user_id;
        const canEdit = isAdmin || isMine;
        const isStatusLoading = actionLoading[r.session_id] === 'status';

        if (canEdit) {
          return (
            <Select
              value={r.follow_up_status}
              size="small"
              style={{ width: 155 }}
              loading={isStatusLoading}
              disabled={isStatusLoading}
              onChange={v => updateStatus(r.session_id, v)}
              options={STATUS_OPTIONS.map(o => ({
                value: o.value,
                label: <span style={{ color: STATUS_CONFIG[o.value]?.color, fontWeight: 600 }}>● {o.label}</span>
              }))}
            />
          );
        }
        return <span className={`aj-badge ${r.follow_up_status}`}><span className="aj-badge-dot" />{STATUS_CONFIG[r.follow_up_status]?.label}</span>;
      }
    },
    {
      title: 'Assigned CSR', width: 175,
      render: (_, r) => {
        const isAssignLoading = actionLoading[r.session_id] === 'assign';

        // ── Admin: full dropdown with real CSR list ───────────────────
        if (isAdmin) {
          return (
            <Select
              labelInValue
              value={r.assigned_csr_id
                ? { value: r.assigned_csr_id, label: r.assigned_csr_name || r.assigned_csr_id }
                : undefined
              }
              size="small"
              style={{ width: 162 }}
              placeholder="Search CSR…"
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={(input, opt) =>
                (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              loading={isAssignLoading || csrListLoading}
              disabled={isAssignLoading}
              onChange={v => assignCSR(r.session_id, v?.value ?? null)}
              options={effectiveCsrList.map(c => ({ value: c.user_id, label: `${c.name} ${c.surname}` }))}
            />
          );
        }
        // ── CSR: assigned to someone else → read-only ────────────────
        if (r.assigned_csr_id && r.assigned_csr_id !== currentUser?.user_id) {
          return (
            <Tag style={{ fontSize: 11, color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6 }}>
              {r.assigned_csr_name || r.assigned_csr_id}
            </Tag>
          );
        }
        // ── CSR: assigned to self → can unassign ───────────────────
        if (r.assigned_csr_id && r.assigned_csr_id === currentUser?.user_id) {
          return (
            <Select
              value={r.assigned_csr_id}
              size="small"
              style={{ width: 162 }}
              allowClear
              loading={isAssignLoading}
              disabled={isAssignLoading}
              onChange={v => assignCSR(r.session_id, v ?? null)}
              options={[{ value: currentUser.user_id, label: `${currentUser.name} (me)` }]}
            />
          );
        }
        // ── CSR: unassigned → "Assign to me" button ────────────────
        return (
          <Button
            size="small"
            className="aj-action-btn"
            loading={isAssignLoading}
            onClick={() => assignCSR(r.session_id, currentUser?.user_id)}
            style={{ width: 120 }}
          >
            Assign to me
          </Button>
        );
      }
    },
    {
      title: 'Actions', width: 100, align: 'center',
      render: (_, r) => (
        <Space>
          <Tooltip title="View session detail">
            <Button className="aj-action-btn" icon={<EyeOutlined />}
              onClick={() => { setSelected(r); setDrawerOpen(true); }} />
          </Tooltip>
          <Tooltip title="Re-book (coming soon)">
            <Button className="aj-action-btn" icon={<CalendarOutlined />} disabled />
          </Tooltip>
        </Space>
      )
    },
  ];

  // ── Tab Items ──────────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'sessions',
      label: <span><UserDeleteOutlined /> Abandoned Sessions</span>,
      children: (
        <>
          <StatsRow summary={summary} loading={loading} />

          {/* Filters */}
          <div className="aj-filters">
            <Input
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Search session ID, patient…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              allowClear
              style={{ width: 260 }}
            />
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="DD MMM YYYY"
              placeholder={['Start date', 'End date']}
              allowClear
              style={{ width: 260 }}
            />
            <Select
              placeholder="Filter by status"
              allowClear
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: 160 }}
              options={[
                { value: 'available',   label: '● Available' },
                { value: 'hot',         label: '● Hot' },
                { value: 'in_progress', label: '● In Progress' },
                { value: 'contacted',   label: '● Contacted' },
                { value: 'converted',   label: '● Converted' },
              ]}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchData(page)}
              style={{ borderRadius: 8 }}
            >Refresh</Button>
          </div>

          {/* Export progress */}
          {exporting && (
            <div style={{ marginBottom: 12 }}>
              <Progress percent={exportProgress} strokeColor={THEME} size="small" />
            </div>
          )}

          {/* Table */}
          <div className="aj-table-card">
            <Table
              dataSource={rows}
              columns={columns}
              rowKey="session_id"
              loading={loading}
              size="middle"
              scroll={{ x: 1100 }}
              pagination={{
                current: page, pageSize: perPage, total,
                showSizeChanger: false,
                className: 'aj-pagination',
                showTotal: (t) => `${t} abandoned sessions`,
                onChange: p => setPage(p),
              }}
              locale={{ emptyText: (
                <div className="aj-empty">
                  <UserDeleteOutlined className="aj-empty-icon" />
                  <div>No abandoned sessions found</div>
                  <div style={{ fontSize: 12 }}>Try adjusting your filters</div>
                </div>
              )}}
            />
          </div>
        </>
      )
    },
    ...(isAdmin ? [{
      key: 'scores',
      label: <span><TeamOutlined /> CSR Scores</span>,
      children: <CSRScoresTab csrList={csrList} csrListLoading={csrListLoading} />
    }] : [])
  ];

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <App>
      <div className="aj-page">
        {/* Header */}
        <div className="aj-header">
          <div className="aj-header-left">
            <div className="aj-header-icon"><UserDeleteOutlined /></div>
            <div>
              <Title level={4} className="aj-title">Abandoned Journeys</Title>
              <Text className="aj-subtitle">Track & follow up on abandoned booking sessions</Text>
            </div>
          </div>
          <div className="aj-header-actions">
            <Tooltip title="Auto-refresh every 2 minutes">
              <Space>
                <SyncOutlined spin={autoRefresh} style={{ color: THEME }} />
                <Switch
                  size="small"
                  checked={autoRefresh}
                  onChange={setAutoRefresh}
                  style={{ backgroundColor: autoRefresh ? THEME : undefined }}
                />
              </Space>
            </Tooltip>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
              disabled={!rows.length}
              style={{ borderRadius: 8, borderColor: THEME, color: THEME }}
            >
              Export CSV
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          className="aj-tabs"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />

        {/* Detail Drawer */}
        <AbandonedDetailDrawer
          open={drawerOpen}
          session={selected}
          csrList={effectiveCsrList}
          isAdmin={isAdmin}
          currentUser={currentUser}
          comments={selected ? [
            // pre-seeded dummy notes + any user-added notes
            ...generateDummyComments(selected.session_id, selected.follow_up_status),
            ...(sessionComments[selected.session_id] || []),
          ] : []}
          onAddComment={addComment}
          onClose={() => setDrawerOpen(false)}
          onStatusChange={updateStatus}
          onAssign={assignCSR}
        />
      </div>
    </App>
  );
}
